import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import { fetchJson, fetchMarkdown, type MarkdownPost } from "../utils/data";

// --- TYPES ---
export interface HomeData {
    hero: {
        badge: string;
        title: string;
        description_prefix: string;
        description_highlight1: string;
        description_highlight2: string;
        description_suffix: string;
    };
    featured: { label: string; title: string; description: string; };
    socials: { platform: string; url: string; }[];
    ticker: string;
    ads: { title: string; subtitle: string; url: string; };
}

export interface ProjectsHeader {
    header: { badge: string; title: string; description: string; }
}

export interface BlogHeader {
    header: { badge: string; title: string; description: string; }
}

export interface AboutData {
    header: { badge: string; title: string; description: string; };
    bio: { title: string; content: string; };
    skills: { title: string; list: string[]; };
    timeline: { title: string; items: { year: string; title: string; desc: string; }[]; };
}

export interface SiteData {
    home: HomeData | null;
    about: AboutData | null;
    projects: {
        header: ProjectsHeader | null;
        list: MarkdownPost[];
    };
    blog: {
        header: BlogHeader | null;
        list: MarkdownPost[];
    };
    lastUpdated: number;
}

// --- CONTEXT ---
interface DataContextType {
    data: SiteData;
    isLoading: boolean;
    refreshData: () => Promise<void>;
}

const DataContext = createContext<DataContextType | null>(null);

const CACHE_KEY = "site_data_cache_v1";

// --- PROVIDER ---
export function DataProvider({ children }: { children: ReactNode }) {
    const [data, setData] = useState<SiteData>({
        home: null,
        about: null,
        projects: { header: null, list: [] },
        blog: { header: null, list: [] },
        lastUpdated: 0
    });
    const [isLoading, setIsLoading] = useState(true);

    // 1. Initial Load from Cache + Background Fetch
    useEffect(() => {
        const loadData = async () => {
            // A. Try Cache First
            const cached = localStorage.getItem(CACHE_KEY);
            if (cached) {
                try {
                    const parsed = JSON.parse(cached);
                    setData(parsed);
                    setIsLoading(false); // Immediate render with cache
                    console.log("Loaded data from cache");
                } catch (e) {
                    console.warn("Failed to parse cache", e);
                }
            }

            // B. Network Fetch (Stale-While-Revalidate)
            await fetchAllData();
        };

        loadData();
    }, []);

    const fetchAllData = async () => {
        try {
            console.log("Fetching fresh data...");
            const [home, about, projectsHeader, projectsList, blogHeader, blogList] = await Promise.all([
                fetchJson<HomeData>('/data/home.json'),
                fetchJson<AboutData>('/data/about.json'),
                fetchJson<ProjectsHeader>('/data/projects.json'),
                fetchProjectsList(),
                fetchJson<BlogHeader>('/data/blog.json'),
                fetchBlogList()
            ]);

            const newData: SiteData = {
                home,
                about,
                projects: { header: projectsHeader, list: projectsList },
                blog: { header: blogHeader, list: blogList },
                lastUpdated: Date.now()
            };

            // Update State
            setData(newData);
            // Update Cache
            localStorage.setItem(CACHE_KEY, JSON.stringify(newData));
            setIsLoading(false);
            console.log("Data updated from network");

        } catch (error) {
            console.error("Global fetch failed:", error);
            setIsLoading(false);
        }
    };

    // Helpers to fetch Lists (logic copied from utils/data but optimized here)
    const fetchProjectsList = async () => {
        const response = await fetchJson<{ items: string[] }>('/data/projects.json');
        return Promise.all(response.items.map(slug => fetchMarkdown(`/data/projects/${slug}.md`, slug)));
    };

    const fetchBlogList = async () => {
        const response = await fetchJson<{ items: string[] }>('/data/blog.json');
        return Promise.all(response.items.map(slug => fetchMarkdown(`/data/blog/${slug}.md`, slug)));
    };

    return (
        <DataContext.Provider value={{ data, isLoading, refreshData: fetchAllData }}>
            {children}
        </DataContext.Provider>
    );
}

// --- HOOK ---
export function useData() {
    const context = useContext(DataContext);
    if (!context) {
        throw new Error("useData must be used within a DataProvider");
    }
    return context;
}
